import React, { useState, useEffect } from 'react';
import { Box, Button, Collapse, Typography, Container, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppointmentCard from './AppointmentCard';
import ErrorDialog from '../../../global/ErrorDialog';
import { Appointment, isRealAppointment, isUpcomingStatus } from '../../../../types';
import { dayjs } from '../../../../utils/dayjs-config';
import CloseIcon from '@mui/icons-material/Close';
import { useResultSnackbar } from '../../../global/SuccessOrErrorSnackbar';

interface MyAppointmentsSectionProps {
    appointments: Appointment[];
    employeeAppointmentsMap: Record<string, string[]>;
}

const MyAppointmentsSection: React.FC<MyAppointmentsSectionProps> = ({
    appointments,
    employeeAppointmentsMap,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
    const [tomorrowAppointments, setTomorrowAppointments] = useState<Appointment[]>([]);
    const [nextAppointments, setNextAppointments] = useState<{ date: string; apps: Appointment[] } | null>(null);
    const [searchFilters, setSearchFilters] = useState({ date: '', firstName: '', lastName: '', email: '', phone: '', id: '' });
    const [filteredAppointments, setFilteredAppointments] = useState<Appointment[] | null>(null);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const [showClearButton, setShowClearButton] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [handoffSuccessDialogOpen, setHandoffSuccessDialogOpen] = useState(false);
    const { showSuccess, showError, snackbar } = useResultSnackbar({
        successTitle: 'Schedule Updated!',
        errorTitle: 'Something Went Wrong',
        autoHideDuration: 2500,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    });
    const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);

    const handleErrorClose = () => {
        setErrorDialogOpen(false);
        setError(null);
    };
    const handleHandoffSuccess = () => setHandoffSuccessDialogOpen(true);
    const handleOpenAppointmentDetails = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setAppointmentDialogOpen(true);
    };
    const handleCloseAppointmentDetails = () => {
        setAppointmentDialogOpen(false);
    };

    const isUpcomingAppointment = (app: Appointment | undefined): app is Appointment =>
        !!app && isRealAppointment(app) && isUpcomingStatus(app.status);

    useEffect(() => {
        const today = dayjs.tz();
        const tomorrow = today.add(1, 'day');
        const todayKey = today.format('MMDDYYYY');
        const tomorrowKey = tomorrow.format('MMDDYYYY');

        const todayApps = (employeeAppointmentsMap[todayKey] || [])
            .map(id => appointments.find(app => app.id === id))
            .filter(isUpcomingAppointment);

        const tomorrowApps = (employeeAppointmentsMap[tomorrowKey] || [])
            .map(id => appointments.find(app => app.id === id))
            .filter(isUpcomingAppointment);

        let nextDate = tomorrow.add(1, 'day');
        let nextDateKey = nextDate.format('MMDDYYYY');
        let nextApps: Appointment[] = [];

        while (!employeeAppointmentsMap[nextDateKey] && nextDate.diff(today, 'days') < 365) {
            nextDate = nextDate.add(1, 'day');
            nextDateKey = nextDate.format('MMDDYYYY');
        }

        if (employeeAppointmentsMap[nextDateKey]) {
            nextApps = employeeAppointmentsMap[nextDateKey]
                .map(id => appointments.find(app => app.id === id))
                .filter(isUpcomingAppointment);
        }

        const sortByTime = (a: Appointment, b: Appointment) => parseInt(a.appointmentTime, 10) - parseInt(b.appointmentTime, 10);
        todayApps.sort(sortByTime);
        tomorrowApps.sort(sortByTime);
        nextApps.sort(sortByTime);

        setTodayAppointments(todayApps);
        setTomorrowAppointments(tomorrowApps);
        setNextAppointments(nextApps.length ? { date: nextDate.format('ddd, MMM D, YYYY'), apps: nextApps } : null);
        setFilteredAppointments(null);
    }, [appointments, employeeAppointmentsMap]);

    const handleSearch = () => {
        const formattedDate = searchFilters.date ? dayjs(searchFilters.date).format('MMDDYYYY') : '';

        const filtered = appointments.filter(app =>
            (!formattedDate || app.appointmentDate === formattedDate) &&
            (!searchFilters.firstName || app.customerFirstName.toLowerCase().includes(searchFilters.firstName.toLowerCase())) &&
            (!searchFilters.lastName || app.customerLastName.toLowerCase().includes(searchFilters.lastName.toLowerCase())) &&
            (!searchFilters.email || app.customerEmail.toLowerCase().includes(searchFilters.email.toLowerCase())) &&
            (!searchFilters.phone || app.customerPhoneNumber.includes(searchFilters.phone)) &&
            (!searchFilters.id || app.id.includes(searchFilters.id))
        );

        filtered.sort((a, b) => {
            const dateA = new Date(
                Number(a.appointmentDate.slice(4, 8)),  // Year (YYYY)
                Number(a.appointmentDate.slice(0, 2)) - 1,  // Month (MM) - 1 (zero-based index)
                Number(a.appointmentDate.slice(2, 4))  // Day (DD)
            );

            const dateB = new Date(
                Number(b.appointmentDate.slice(4, 8)),
                Number(b.appointmentDate.slice(0, 2)) - 1,
                Number(b.appointmentDate.slice(2, 4))
            );

            const dateDiff = dateB.getTime() - dateA.getTime(); // Sort by date (newest first)
            return dateDiff !== 0 ? dateDiff : Number(b.appointmentTime) - Number(a.appointmentTime); // Sort by time (latest first)
        });

        if (filtered.length > 0) {
            setFilteredAppointments(filtered);
            setShowClearButton(true);
        }
        else {
            setError("No appointments found");
            setErrorDialogOpen(true);
        }
        setSearchFilters({ date: '', firstName: '', lastName: '', email: '', phone: '', id: '' });
        setSearchDialogOpen(false);
    };

    return (
        <Container sx={{ pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 0 } }}>
            <ErrorDialog open={errorDialogOpen} onClose={handleErrorClose} message={error} />
            <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {!isMobile && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setSearchDialogOpen(true)}
                        sx={{
                            borderRadius: '4px',
                            border: '2px solid rgba(255,255,255,0.15)',
                            fontWeight: '600',
                        }}
                    >
                        Search
                    </Button>
                )}
                <Collapse in={showClearButton}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowClearButton(false);
                            setSearchFilters({ date: '', firstName: '', lastName: '', email: '', phone: '', id: '' });
                            setFilteredAppointments(null);
                        }}
                        sx={{
                            borderRadius: '4px',
                            border: '2px solid #FFF',
                            fontWeight: '600',
                        }}
                    >
                        Clear
                    </Button>
                </Collapse>
            </Box>

            {!isMobile && (
                <hr
                    style={{
                        border: "none",
                        height: "2px",
                        backgroundColor: "rgba(0,0,0,0.2)",
                        boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                        marginBottom: "20px",
                    }}
                />
            )}

            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        px: 2,
                        py: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 3,
                        zIndex: 10,
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setSearchDialogOpen(true)}
                        fullWidth
                        sx={{
                            borderRadius: '4px',
                            border: '2px solid rgba(255,255,255,0.15)',
                            fontWeight: '600',
                        }}
                    >
                        Search
                    </Button>
                </Box>
            )}

            <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)}>
                <DialogTitle sx={{ textAlign: "center" }}>Search Appointments</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Choose Date" type="date" InputLabelProps={{ shrink: true }} onChange={(e) => setSearchFilters({ ...searchFilters, date: e.target.value })} margin="dense" />
                    <TextField fullWidth label="First Name" onChange={(e) => setSearchFilters({ ...searchFilters, firstName: e.target.value })} margin="dense" />
                    <TextField fullWidth label="Last Name" onChange={(e) => setSearchFilters({ ...searchFilters, lastName: e.target.value })} margin="dense" />
                    <TextField fullWidth label="Email" onChange={(e) => setSearchFilters({ ...searchFilters, email: e.target.value })} margin="dense" />
                    <TextField fullWidth label="Phone" onChange={(e) => setSearchFilters({ ...searchFilters, phone: e.target.value })} margin="dense" />
                    <TextField fullWidth label="Appointment ID" onChange={(e) => setSearchFilters({ ...searchFilters, id: e.target.value })} margin="dense" />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => { setSearchFilters({ date: '', firstName: '', lastName: '', email: '', phone: '', id: '' }); setSearchDialogOpen(false); }}
                        sx={{
                            borderRadius: '4px',
                            border: '2px solid #FFF',
                            fontWeight: '600',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            const hasFilter = Object.values(searchFilters).some(value => value?.trim());
                            if (!hasFilter) {
                                setError("No filter selected");
                                setErrorDialogOpen(true);
                                return;
                            }
                            handleSearch();
                        }}
                        sx={{
                            borderRadius: '4px',
                            border: '2px solid rgba(255,255,255,0.15)',
                            fontWeight: '600',
                        }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {filteredAppointments ? (
                <Box mb={4}>
                    <Typography variant="h6" align="center" color="gray">SEARCH RESULTS</Typography>
                    {filteredAppointments.map(appointment => (
                        <AppointmentCard
                            key={appointment.id}
                            appointment={appointment}
                            onOpenDetails={handleOpenAppointmentDetails}
                            showSuccess={showSuccess}
                            showError={showError}
                            onHandoffSuccess={handleHandoffSuccess}
                        />
                    ))}
                </Box>
            ) : (
                <>
                    {/* Today */}
                    <Box mb={4}>
                        <Typography variant="h6" align="center" color="gray">TODAY</Typography>
                        {todayAppointments.length > 0 ? todayAppointments.map(appointment => (
                            <AppointmentCard
                                key={appointment.id}
                                appointment={appointment}
                                onOpenDetails={handleOpenAppointmentDetails}
                                showSuccess={showSuccess}
                                showError={showError}
                                onHandoffSuccess={handleHandoffSuccess}
                            />
                        )) : (
                            <Typography align="center">No appointments</Typography>
                        )}
                    </Box>

                    {/* Tomorrow */}
                    <Box mb={4}>
                        <Typography variant="h6" align="center" color="gray">TOMORROW</Typography>
                        {tomorrowAppointments.length > 0 ? tomorrowAppointments.map(appointment => (
                            <AppointmentCard
                                key={appointment.id}
                                appointment={appointment}
                                onOpenDetails={handleOpenAppointmentDetails}
                                showSuccess={showSuccess}
                                showError={showError}
                                onHandoffSuccess={handleHandoffSuccess}
                            />
                        )) : (
                            <Typography align="center">No appointments</Typography>
                        )}
                    </Box>

                    {/* Next Available Date */}
                    {nextAppointments && (
                        <Box mb={4}>
                            <Typography variant="h6" align="center" color="gray" sx={{ textTransform: 'uppercase' }}> {nextAppointments.date}</Typography>
                            {nextAppointments.apps.map(appointment => (
                                <AppointmentCard
                                    key={appointment.id}
                                    appointment={appointment}
                                    onOpenDetails={handleOpenAppointmentDetails}
                                    showSuccess={showSuccess}
                                    showError={showError}
                                    onHandoffSuccess={handleHandoffSuccess}
                                />
                            ))}
                            <Typography variant='body2' align="center" color="gray"> More dates can be found under the My Schedule tab</Typography>
                        </Box>
                    )}
                </>
            )}

            <Dialog
                open={appointmentDialogOpen}
                onClose={handleCloseAppointmentDetails}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Fade}
                TransitionProps={{
                    timeout: 220,
                    onExited: () => setSelectedAppointment(null),
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 2,
                        width: '100%',
                        m: 0,
                        mx: 1,
                        height: 'auto',
                        maxHeight: { xs: '92dvh', sm: 'calc(100% - 64px)' },
                    }
                }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Manage Appointment
                    <IconButton onClick={handleCloseAppointmentDetails} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {selectedAppointment && (
                    <DialogContent sx={{ p: { xs: 0, sm: 2 } }}>
                        <AppointmentCard
                            appointment={selectedAppointment}
                            fullWidth
                            defaultExpanded
                            renderAsDialogContent
                            showSuccess={showSuccess}
                            showError={showError}
                            onHandoffSuccess={handleHandoffSuccess}
                        />
                    </DialogContent>
                )}
            </Dialog>
            <Dialog
                open={handoffSuccessDialogOpen}
                onClose={() => setHandoffSuccessDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Appointment Handed Off</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        The appointment was successfully handed off to the selected staff member.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setHandoffSuccessDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {snackbar}
        </Container>
    );
};

export default MyAppointmentsSection;
